import React from "react";
import { Activity, CheckCircle2, Shield, Users } from "lucide-react";
import "./About.css";

const About = () => (
  <div className="about-page">
    <section className="hero">
      <div className="hero-content">
        <p className="hero-tag">About Us</p>
        <h1>We Started With a Simple Idea</h1>
        <p>
          What if a freight company actually answered the phone? What if carriers got paid on time, every time?
          What if shippers could trust that their load wouldn't fall through? In 2019, we set out to build that company.
        </p>
      </div>
    </section>

    <section className="story-section">
      <div className="story-content">
        <div className="story-text">
          <h2>The Short Version</h2>
          <p>
            Sunny Logistics was founded by industry veterans who got tired of watching the same problems repeat themselves:
            brokers who ghost their carriers, shippers left scrambling for capacity, and nobody picking up the phone when things go wrong.
          </p>
          <p>
            <strong>We started in Indianapolis.</strong> No fancy office, no venture capital—just a commitment to do things differently.
            Treat people fairly. Pay on time. Answer when they call.
          </p>
          <p>
            Six years later, we've delivered over 3,000 loads, built a network of trusted carriers, and earned the business of 20+ shippers
            who count on us every week. We're still based in Fishers, Indiana, still answering our phones, and still growing the right way.
          </p>
          <div className="story-highlight">
            <p>"We're not trying to be the biggest. We're trying to be the company people actually want to work with."</p>
          </div>
        </div>

        <div className="founder-card">
          <h3>Founded by Operators</h3>
          <p className="title">Built from the driver’s seat up.</p>
          <p>
            Our leadership team has spent decades in trucking—dispatching loads, managing fleets, solving problems at 2 AM.
            We know what it's like on both sides of the transaction, and we built Sunny to fix the things that drove us crazy.
          </p>

          <div className="founder-stats">
            <div className="founder-stat">
              <div className="founder-stat-number">15+</div>
              <div className="founder-stat-label">Years Combined Experience</div>
            </div>
            <div className="founder-stat">
              <div className="founder-stat-number">Midwest</div>
              <div className="founder-stat-label">Born &amp; Based</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="mission-section">
      <div className="mission-content">
        <div className="mission-grid">
          <div className="mission-card">
            <h3>Our Mission</h3>
            <p>
              To build a freight network where shippers get reliable capacity, carriers get paid fairly and on time,
              and everyone can reach a real person when they need help. We use technology to make logistics more efficient—but
              never as a replacement for human relationships.
            </p>
          </div>
          <div className="mission-card">
            <h3>Our Vision</h3>
            <p>
              To become the most trusted logistics partner in the Midwest—not the biggest, but the one people recommend to their friends.
              The company you call when you're tired of getting burned by the big guys.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="values-section">
      <div className="values-content">
        <div className="values-header">
          <h2>What We Actually Believe</h2>
          <p>Not corporate buzzwords. These are the principles we use to make decisions.</p>
        </div>
        <div className="values-grid">
          <div className="value-card">
            <div className="value-icon">
              <Shield />
            </div>
            <h4>Do What You Say</h4>
            <p>Rate confirmations are final. Pickup times are real. If we say we'll call you back, we call you back. No exceptions.</p>
          </div>
          <div className="value-card">
            <div className="value-icon">
              <Activity />
            </div>
            <h4>No Surprises</h4>
            <p>Bad news doesn't get better with time. If there's a problem, we tell you immediately and work to fix it—not hide it.</p>
          </div>
          <div className="value-card">
            <div className="value-icon">
              <Users />
            </div>
            <h4>Everyone Eats</h4>
            <p>We don't squeeze carriers to pad margins. Fair rates for them means better service for shippers. It's not complicated.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="timeline-section">
      <div className="timeline-content">
        <div className="timeline-header">
          <h2>Our Journey</h2>
        </div>
        <div className="timeline">
          <div className="timeline-item">
            <div className="timeline-year">2019</div>
            <div className="timeline-dot"></div>
            <div className="timeline-content-item">
              <h4>Started With a Promise</h4>
              <p>Founded Sunny Logistics with a laptop and a clear promise to do the basics right. First customer was a manufacturer in Indianapolis who took a chance on us.</p>
              <span className="timeline-highlight">First load delivered</span>
            </div>
          </div>
          <div className="timeline-item">
            <div className="timeline-year">2020</div>
            <div className="timeline-dot"></div>
            <div className="timeline-content-item">
              <h4>Grew Through the Chaos</h4>
              <p>When COVID hit, supply chains went haywire. We stayed on the phones 24/7, kept our commitments, and earned customers who remembered how we showed up when things got hard.</p>
              <span className="timeline-highlight">Start of the expansion</span>
            </div>
          </div>
          <div className="timeline-item">
            <div className="timeline-year">2021</div>
            <div className="timeline-dot"></div>
            <div className="timeline-content-item">
              <h4>Added the Tech</h4>
              <p>Added tech to give shippers real-time tracking and carriers easier booking. Crossed 1,000 loads delivered.</p>
              <span className="timeline-highlight">1,000+ loads delivered</span>
            </div>
          </div>
          <div className="timeline-item">
            <div className="timeline-year">2023</div>
            <div className="timeline-dot"></div>
            <div className="timeline-content-item">
              <h4>Hit Our Stride</h4>
              <p>Reached 2,000 loads, 20+ active shippers, and built a carrier network we actually trust. Launched Quick Pay to make sure our carriers never wait for their money.</p>
              <span className="timeline-highlight">24-hour Quick Pay launched</span>
            </div>
          </div>
          <div className="timeline-item">
            <div className="timeline-year">2026</div>
            <div className="timeline-dot"></div>
            <div className="timeline-content-item">
              <h4>Today</h4>
              <p>3,000+ loads delivered, 98.5% on-time rate, and still answering our phones. Growing smart, staying Midwest-focused, and building something that lasts.</p>
              <span className="timeline-highlight">3,000+ loads &amp; counting</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="difference-section">
      <div className="difference-content">
        <div className="difference-header">
          <h2>How We're Different</h2>
          <p>Not just marketing. Things we actually do.</p>
        </div>
        <div className="difference-grid">
          <div className="difference-card">
            <h4>
              <CheckCircle2 />
              We Answer the Phone
            </h4>
            <p>Call us right now. A real person will pick up. Not a phone tree, not a callback queue. This is non-negotiable for us.</p>
          </div>
          <div className="difference-card">
            <h4>
              <CheckCircle2 />
              Reliable Capacity
            </h4>
            <p>We cover your freight with a vetted carrier network—not whoever's cheapest on the board. Consistency over shortcuts.</p>
          </div>
          <div className="difference-card">
            <h4>
              <CheckCircle2 />
              Carriers Get Paid Fast
            </h4>
            <p>24-hour Quick Pay. Our carriers trust us because we've never missed a payment or changed a rate after the fact.</p>
          </div>
          <div className="difference-card">
            <h4>
              <CheckCircle2 />
              We Know Our Lanes
            </h4>
            <p>Midwest regional focus means we know the shippers, the facilities, the traffic patterns. We're not spread thin across the whole country.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="cta-section">
      <div className="cta-content">
        <h2>Want to Work With Us?</h2>
        <p>Whether you're shipping freight or hauling it, we'd love to hear from you.</p>
        <div className="cta-buttons">
          <a href="/shippers" className="btn btn-primary btn-large">I Need to Ship Freight</a>
          <a href="/carriers" className="btn btn-outline btn-large">I Want to Haul Loads</a>
        </div>
      </div>
    </section>

    <footer className="about-footer">
      <div className="footer-content">
        <div className="footer-col">
          <h4>Quick Links</h4>
          <ul>
            <li><a href="/">Home</a></li>
            <li><a href="/shippers">Ship with us</a></li>
            <li><a href="/carriers">Register as a Carrier</a></li>
          </ul>
        </div>
        <div className="footer-col">
          <h4>Our Office</h4>
          <address>
            11 MUNICIPAL DRIVE<br />
            SUITE 200<br />
            FISHERS, IN 46038
          </address>
        </div>
        <div className="footer-col">
          <h4>Legal</h4>
          <ul>
            <li><a href="/terms">Terms of Service</a></li>
            <li><a href="/privacy">Privacy Policy</a></li>
          </ul>
        </div>
      </div>
      <div className="footer-bottom">
        <p>© 2026 Sunny Logistics. All rights reserved.</p>
      </div>
    </footer>
  </div>
);

export default About;
